import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Radar, CheckCircle2, Circle, Search, PackageCheck, MinusCircle, Link2 } from "lucide-react";
import { toast } from "sonner";
import { appApi, inr, fmtDateTime, formatApiErrorDetail } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

const COURIERS = ["XpressBees", "Ekart", "Amazon ATS", "BlueDart", "Delhivery", "Shadowfax", "DTDC", "Ecom Express", "India Post", "IndiaMovers (CC)", "ShipZilla Cargo"];

const STEPS = [
  { key: "manifested", label: "Manifest created", match: ["manifest", "created"] },
  { key: "pickup_scheduled", label: "Pickup scheduled", match: ["pickup scheduled", "pickup_created"] },
  { key: "picked_up", label: "Picked up by courier", match: ["picked", "pickup_complete", "pickup complete"] },
  { key: "in_transit", label: "In transit", match: ["in transit", "in_transit", "received", "shipment"] },
  { key: "out_for_delivery", label: "Out for delivery", match: ["out for delivery", "out_for_delivery", "ofd"] },
  { key: "delivered", label: "Delivered", match: ["delivered"] },
];

const normalize = (s) => String(s || "").toLowerCase().replace(/-/g, "_");

const currentStepIndex = (status) => {
  const s = normalize(status);
  if (s === "delivered") return 5;
  if (s === "out_for_delivery") return 4;
  if (s === "in_transit" || s === "reached_hub") return 3;
  if (s === "picked_up" || s === "pickup_completed" || s === "pickup_complete") return 2;
  if (s === "pickup_scheduled" || s === "pickup_pending") return 1;
  return 0; // created / manifested / pending_assignment
};

const eventMatches = (ev, step) => {
  const txt = `${ev.status || ""} ${ev.description || ""}`.toLowerCase();
  return step.match.some((m) => txt.includes(m));
};

export default function Tracking() {
  const [params, setParams] = useSearchParams();
  const [awb, setAwb] = useState(params.get("awb") || "");
  const [courier, setCourier] = useState("");
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchTrack = async (code) => {
    if (!code) return;
    setLoading(true);
    setError("");
    setDetail(null);
    try {
      const d = await appApi.get(`track/${encodeURIComponent(code)}`);
      setDetail(d);
      const known = COURIERS.find((c) => String(d.courier_name || "").toLowerCase().includes(c.toLowerCase()));
      if (known) setCourier(known);
    } catch (err) {
      // B2B freight / rate-card shipments live in the tracker, not /track — fall back
      if (err.response?.status === 404) {
        try {
          const s = await appApi.get("tracker/shipments", { q: code });
          const match = (s.shipments || []).find((x) => String(x.awb || "").toLowerCase() === code.toLowerCase());
          if (match) {
            const det = await appApi.get(`tracker/shipments/${match.kind}/${match.id}`);
            setDetail({ ...(det.shipment || det), kind: match.kind });
            return;
          }
        } catch { /* fall through to error */ }
      }
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initial = params.get("awb");
    if (initial) fetchTrack(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e) => {
    e.preventDefault();
    setParams({ awb });
    fetchTrack(awb);
  };

  const events = detail?.events || [];
  const status = normalize(detail?.status);
  const isTerminalBad = status === "rto" || status === "ndr" || status === "cancelled";
  const idx = detail ? currentStepIndex(detail.status) : -1;

  // merge real courier events into the canonical stepper
  const steps = STEPS.map((step, i) => {
    const ev = events.find((e) => eventMatches(e, step));
    return {
      ...step,
      event: ev,
      state: i < idx ? "done" : i === idx ? "current" : "pending",
    };
  });

  const statusLabel = String(detail?.status || "").replace(/[_-]/g, " ").toUpperCase();

  return (
    <div data-testid="tracking-page" className="space-y-6">
      <div>
        <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">Tracking</p>
        <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">Track shipment</h1>
        <p className="text-sm text-zinc-500 mt-2">Live courier status, right inside ShipZilla — no third-party site needed.</p>
      </div>

      <form onSubmit={submit} className="flex flex-wrap gap-3 max-w-3xl" data-testid="tracking-form">
        <select
          value={courier}
          onChange={(e) => setCourier(e.target.value)}
          data-testid="tracking-courier-select"
          className="border-2 border-obsidian bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian"
        >
          <option value="">Courier (auto)</option>
          {COURIERS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input
          value={awb}
          onChange={(e) => setAwb(e.target.value)}
          placeholder="Enter tracking number (AWB)"
          data-testid="tracking-awb-input"
          className="flex-1 min-w-[220px] border-2 border-obsidian bg-white px-4 py-3 text-sm font-mono outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian"
        />
        <button type="submit" data-testid="tracking-submit-button" disabled={loading}
          className="inline-flex items-center gap-2 bg-acid text-white font-bold px-6 py-3 border-2 border-obsidian shadow-[4px_4px_0px_0px_#0B1E3A] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150 disabled:opacity-60">
          <Search className="w-4 h-4" /> {loading ? "Tracking…" : "Track"}
        </button>
        {detail && (
          <button type="button" data-testid="tracking-share-link"
            onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/track/${detail.awb}`); toast.success("Customer tracking link copied — share it anywhere."); }}
            className="inline-flex items-center gap-2 bg-white text-obsidian font-bold px-6 py-3 border-2 border-obsidian hover:bg-obsidian hover:text-sky-300 transition-all duration-150 text-xs uppercase tracking-widest">
            <Link2 className="w-4 h-4" /> Share customer link
          </button>
        )}
      </form>

      {error && (
        <div data-testid="tracking-error" className="max-w-xl border-2 border-obsidian bg-white px-4 py-4 text-sm">
          <p className="font-bold">{error === "AWB not found" ? "Courier milestones not published yet" : error}</p>
          <p className="text-zinc-500 text-xs mt-1.5">
            Scans appear in this panel the moment the courier picks up the parcel.
            Amazon B2C shipments are tracked by our ops team and updated here directly.
          </p>
        </div>
      )}

      {detail && (
        <>
          <div data-testid="tracking-status-banner" className="border-2 border-obsidian bg-obsidian text-white px-6 py-5 flex flex-wrap items-center justify-between gap-4 shadow-[6px_6px_0px_0px_#2563EB]">
            <div>
              <p className="text-[10px] font-mono font-bold uppercase tracking-[0.25em] text-zinc-400">Current status · {detail.courier_name}</p>
              <p className="font-display font-black text-3xl tracking-tight mt-1 text-sky-300">{statusLabel}</p>
            </div>
            <div className="text-right font-mono text-xs text-zinc-400 space-y-1">
              <p>AWB <span className="text-white font-bold">{detail.awb}</span></p>
              <p>{detail.pickup?.pincode} → {detail.delivery?.pincode}</p>
              {detail.estimated_delivery && <p>ETA {fmtDateTime(detail.estimated_delivery)}</p>}
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-6" data-testid="tracking-result">
            <div className="border-2 border-obsidian bg-white p-6 h-fit">
              <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500 flex items-center gap-2">
                <Radar className="w-4 h-4 text-blaze" /> Shipment journey
              </p>
              <div className="mt-6" data-testid="tracking-timeline">
                {steps.map((s, i) => (
                  <div key={s.key} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      {s.state === "pending"
                        ? <MinusCircle className="w-5 h-5 text-zinc-300 shrink-0" />
                        : s.state === "current"
                          ? <PackageCheck className="w-5 h-5 text-white fill-acid shrink-0" />
                          : <CheckCircle2 className="w-5 h-5 text-obsidian fill-acid shrink-0" />}
                      {i < steps.length - 1 && <div className={`w-0.5 flex-1 min-h-8 ${s.state === "pending" ? "bg-zinc-200" : "bg-obsidian"}`} />}
                    </div>
                    <div className="pb-7">
                      <p className={`text-sm font-bold ${s.state === "current" ? "text-acid" : s.state === "pending" ? "text-zinc-400" : "text-obsidian"}`}
                        data-testid={`step-${s.key}`}>
                        {s.label}
                        {s.state === "current" && <span className="ml-2 text-[10px] font-mono uppercase tracking-widest bg-acid text-white px-1.5 py-0.5">Now</span>}
                      </p>
                      <p className="text-xs font-mono text-zinc-500 mt-1">
                        {s.event
                          ? `${s.event.location || ""} · ${fmtDateTime(s.event.timestamp)}`
                          : s.state === "pending"
                            ? "Pending"
                            : "Confirmed by courier status"}
                      </p>
                      {s.event?.description && <p className="text-xs text-zinc-500 mt-0.5">{s.event.description}</p>}
                    </div>
                  </div>
                ))}
                {isTerminalBad && (
                  <div className="flex gap-4">
                    <MinusCircle className="w-5 h-5 text-red-600 shrink-0" />
                    <p className="text-sm font-bold text-red-600">{statusLabel}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="lg:col-span-2 border-2 border-obsidian bg-white p-6 sm:p-8">
              <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">Courier scan history</p>
              <div className="mt-6" data-testid="tracking-events">
                {events.slice().reverse().map((ev, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <Circle className={`w-4 h-4 shrink-0 ${i === 0 ? "text-acid" : "text-zinc-300"}`} />
                      {i < events.length - 1 && <div className="w-0.5 flex-1 min-h-6 bg-zinc-200" />}
                    </div>
                    <div className="pb-6">
                      <p className={`text-sm font-semibold ${i === 0 ? "text-obsidian" : "text-zinc-500"}`}>{ev.description || ev.status}</p>
                      <p className="text-xs font-mono text-zinc-500 mt-0.5">{ev.location} · {fmtDateTime(ev.timestamp)}</p>
                    </div>
                  </div>
                ))}
                {events.length === 0 && (
                  <p className="text-sm text-zinc-500">
                    Detailed scans not published by the courier yet — the journey on the left shows the courier's confirmed status.
                  </p>
                )}
              </div>

              <div className="mt-6 pt-6 border-t-2 border-obsidian grid grid-cols-2 gap-3 text-sm">
                <div className="flex justify-between"><span className="text-zinc-500">Type</span><span className="font-mono text-xs uppercase">{detail.shipment_type} · {detail.mode}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Weight</span><span className="font-mono text-xs">{detail.package?.weight_kg ?? "—"} kg</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Rate</span><span className="font-mono text-xs font-bold">{detail.rate != null ? inr(detail.rate) : "—"}</span></div>
                <div className="flex justify-between items-center"><span className="text-zinc-500">Status</span><StatusBadge status={detail.status} /></div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
